import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';

import FrameIcon from '~/assets/icons/design-qr/frame.svg?react';
import DesignStepBlockHeaderLayout from '~/components/QRCodeDesign/DesignStepBlockHeaderLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import { DEFAULT_QR_CODE_STYLES } from '~/constants/common';
import QRFrameTextForm from '~/pages/CreateQR/components/QRFrameTextForm';
import useDesignQRSectionSectionStore from '~/store/useDesignQRSectionStore';
import { COLORS } from '~/styles/colors';
import { QrCodeFrameItem, QrCodeStyle } from '~/types/qrCode';

type QRCodeFrameProps = {
  frames: QrCodeFrameItem[];
  defaultSelectedFrame?: number;
  onSelectFrame: (frame: QrCodeFrameItem) => void;
  onUpdateQrStyles: (newStyles: QrCodeStyle) => void;
  qrStyles: QrCodeStyle;
  defaultExpanded?: boolean;
};

export default function QRCodeFrame({
  frames,
  defaultSelectedFrame,
  onSelectFrame,
  qrStyles,
  onUpdateQrStyles,
  defaultExpanded = false,
}: QRCodeFrameProps) {
  const { t } = useTranslation();

  const [selectedFrameId, setSelectedFrameId] = useState(defaultSelectedFrame || DEFAULT_QR_CODE_STYLES.frame.style);
  const { setSelectedFrame, selectedFrame } = useDesignQRSectionSectionStore();

  const handleChangeFrame = (frame: QrCodeFrameItem) => {
    if (!selectedFrame || selectedFrame.id !== frame.id) {
      setSelectedFrameId(frame.id);
      onSelectFrame(frame);
      setSelectedFrame(frame);
    }
  };

  return (
    <Accordion
      variant="medium"
      defaultExpanded={defaultExpanded}
      summary={
        <DesignStepBlockHeaderLayout
          Icon={FrameIcon}
          title={t('thirdStep:frame')}
          description={t('builder:designStep.frame.description')}
        />
      }
      details={
        <>
          <Box sx={{ position: 'relative', height: '152px' }}>
            <Box sx={{ position: 'absolute', maxWidth: '100%' }}>
              <StyledSwiper spaceBetween={16} slidesPerView="auto">
                {frames?.map((frame) => {
                  return (
                    <StyledSwiperSlide
                      key={frame.id}
                      tag="button"
                      $isSelected={selectedFrameId === frame.id}
                      onClick={() => handleChangeFrame(frame)}
                    >
                      <StyledFrame src={frame.previewImage} alt="qr code frame" />
                    </StyledSwiperSlide>
                  );
                })}
              </StyledSwiper>
            </Box>
          </Box>
          {!!qrStyles && !!onUpdateQrStyles && selectedFrameId !== 34 && (
            <QRFrameTextForm qrStyle={qrStyles} onUpdateStyles={onUpdateQrStyles} />
          )}
        </>
      }
      sx={{ width: '100%' }}
    />
  );
}

export const StyledSwiper = styled(Swiper)(() => {
  return {
    marginTop: '16px',
  };
});

export const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop) => prop !== '$isSelected',
})<{ $isSelected: boolean }>(({ $isSelected }) => {
  return {
    maxWidth: '108px',
    height: '136px',
    borderRadius: '10px',
    padding: '8px',
    border: $isSelected ? `1px solid ${COLORS.blue100}` : 'none',
    backgroundColor: $isSelected ? COLORS.blue50 : COLORS.transparent,
    cursor: 'pointer',
  };
});

export const StyledFrame = styled('img')(() => {
  return {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };
});
